import { useState } from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { faCalendar, faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@nazka/nazka.mapframe.components';

import { getNextAndPrevDate } from './date-picker.utils';

import './date-picker.css';

const toDateString = date => new Date(date).toDateString();

const toIsoDateString = date => dayjs(date).format('YYYY-MM-DD');

const DATE_DISPLAY_FORMAT = 'D MMMM YYYY';
const PICKER_TEXT_FIELD_SX = {
  'width': '100%',
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    paddingRight: 'calc(var(--layout-spacing) * 12)',
    borderRadius: 'var(--layout-spacing)',
    boxShadow: 'none',
  },
  '& .MuiPickersOutlinedInput-notchedOutline': {
    border: 0,
  },
  '& .MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline': {
    border: 0,
  },
  '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
    border: 0,
  },
  '& .MuiInputBase-input::placeholder, & .MuiPickersInputBase-input::placeholder': {
    color: 'var(--color-common-grey-light)',
    opacity: 1,
  },
  '& .MuiPickersInputBase-sectionsContainer': {
    padding: 'calc(var(--layout-spacing) * 1.5) 0',
  },
  '& .MuiPickersSectionList-root': {
    pointerEvents: 'none',
  },
};

const PICKER_DAY_SX = {
  '&:not(.Mui-selected):not(.Mui-disabled)': {
    color: 'var(--color-common-grey-dark)',
    backgroundColor: 'var(--color-primary-light)',
    borderRadius: '50%',
  },
  '&:hover:not(.Mui-selected):not(.Mui-disabled)': {
    color: 'var(--color-common-white)',
    backgroundColor: 'var(--color-primary-main)',
  },
  '&.Mui-disabled': {
    color: 'var(--color-common-grey-light)',
    backgroundColor: 'transparent',
  },
  '&.Mui-selected': {
    color: 'var(--color-primary-main)',
    backgroundColor: 'transparent',
    border: '2px solid var(--color-primary-main)',
    fontWeight: 700,
  },
  '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible:hover, &.Mui-selected:focus:hover': {
    backgroundColor: 'var(--color-primary-light)',
  },
  '&.Mui-focusVisible': {
    outline: 0,
  },
  '&.Mui-selected.Mui-focusVisible, &.Mui-selected:focus': {
    backgroundColor: 'transparent',
  },
  '&:focus-visible:not(.Mui-selected):not(.Mui-disabled)': {
    backgroundColor: 'var(--color-primary-light)',
  },
};

function DatePicker(props) {
  const {
    availableDays = [],
    onChange,
    value,
    placeholder = 'DD/MM/YYYY',
    ...datePickerProps
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const availableDateStrings = availableDays.map(toDateString);
  const { next: nextDate, prev: prevDate } = getNextAndPrevDate(value, availableDateStrings);

  const selectedValue = value ? dayjs(value) : null;
  const minDate = availableDays.length > 0 ? dayjs(availableDays[availableDays.length - 1]) : undefined;
  const maxDate = availableDays.length > 0 ? dayjs(availableDays[0]) : undefined;

  const isDayDisabled = (day) => {
    const dateString = day.toDate().toDateString();
    return availableDateStrings.length > 0 && !availableDateStrings.includes(dateString);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="date-picker__wrapper">
        <MuiDatePicker
          {...datePickerProps}
          value={selectedValue}
          onChange={newValue => onChange(newValue?.isValid?.() ? toIsoDateString(newValue) : null)}
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          format={DATE_DISPLAY_FORMAT}
          disableHighlightToday
          shouldDisableDate={isDayDisabled}
          minDate={minDate}
          maxDate={maxDate}
          views={['month', 'day']}
          openTo="day"
          slotProps={{
            textField: {
              placeholder,
              sx: PICKER_TEXT_FIELD_SX,
            },
            field: {
              readOnly: true,
            },
            day: {
              sx: PICKER_DAY_SX,
            },
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            },
            desktopPaper: {
              sx: {
                boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                background: '#fff',
              },
            },
            openPickerButton: {
              sx: { display: 'none' },
            },
            inputAdornment: {
              sx: { display: 'none' },
            },
          }}
        />
        <div className="date-picker__controls">
          <Button
            title="Previous date"
            className="date-picker__controls__button date-picker__controls__button--prev"
            icon={faChevronLeft}
            onClick={() => onChange(toIsoDateString(prevDate))}
            disabled={!prevDate}
            layout="ghost"
            hover={false}
          />
          <Button
            title="Open calendar"
            className="date-picker__controls__button date-picker__controls__button--calendar"
            icon={faCalendar}
            onClick={() => setIsOpen(true)}
            layout="ghost"
            hover={false}
          />
          <Button
            title="Next date"
            className="date-picker__controls__button date-picker__controls__button--next"
            icon={faChevronRight}
            onClick={() => onChange(toIsoDateString(nextDate))}
            disabled={!nextDate}
            layout="ghost"
            hover={false}
          />
        </div>
      </div>
    </LocalizationProvider>
  );
}

export default DatePicker;
